import angular from 'angular';
import '../../services/reception';
import '../../services/appointment';
import '../../services/identification';
import { isEmpty, get, toUpper, toLower, kebabCase, camelCase, trimStart, cloneDeep } from 'lodash';
import Gender from '../../enums/gender';
import PersonType from '../../enums/personType';
import IdentificationType from '../../enums/identificationType';
import { CalledFromEnum } from '../../utils/calledFromEnum';
import { localStorage } from '../../utils';

const module = angular.module('whyline.controllers.reception-welcome', [
  'whyline.services.reception',
  'whyline.services.appointment',
  'whyline.services.identification',
]);

const ReceptionWelcomeController = (
  ReceptionService,
  $scope,
  $rootScope,
  $translate,
  currentPlace,
  $state,
  PlaceService,
  ValidationService,
  SAFE_STATES,
  currentOrganization,
  NotificationService,
  AppointmentService,
  IdentificationService,
  SocketService,
) => {
  /* @ngInject */

  let toastrInstance = null;

  $scope.isWhylineEnterprise = currentPlace.enterpriseType === 'whyline';

  $scope.currentPlaceId = $state.params.placeId;

  ReceptionService.SetButtonStatus('both', false);
  ReceptionService.SetButtonStatus('status-text', false);
  localStorage.remove('appointments');
  localStorage.remove('identificationData');

  const place = currentPlace;

  $scope.genderKeys = Object.keys(Gender)
    .filter((gender) => gender && gender !== 'none')
    .map((gender) => kebabCase(gender));

  $scope.idLimits = {
    physicalPerson: {
      min: 6,
      max: 8,
    },
    legalPerson: {
      min: 9,
      max: 11,
    },
  };

  $scope.submitIdClicked = false;
  $scope.currentGender = '';
  $scope.showGender = false;
  $scope.isLegalPerson = false;
  $scope.currentPerson = PersonType.physicalPerson;
  $scope.minLength = $scope.idLimits[$scope.currentPerson].min;
  $scope.maxLength = $scope.idLimits[$scope.currentPerson].max;

  const identifications = PlaceService.getIdentificationsForCurrentPlace() || [];

  const selected =
    identifications.find((id) => id.default) || identifications.find((id) => isEmpty(id.key));

  $scope.idTypesList = identifications;

  $scope.showPriority = false;

  $scope.person = {
    id: '',
    idType: selected ? selected.key : undefined,
    phone: {
      countryCode: toUpper(place.location.countryCode),
      full: '',
    },
    email: '',
    priority: {
      category: null,
    },
    gender: '',
  };

  if (localStorage.get('notFoundMessage')) {
    NotificationService.Error($translate.instant('no_results_found_reception'));
    localStorage.remove('notFoundMessage');
  }

  $scope.goToOtherFilters = () => {
    localStorage.remove('notFoundMessage');
    $state.go(SAFE_STATES.receptionAppointmentSearch);
  };

  $scope.keyPressValidate = (event) => {
    event.target.value = event.target.value.trim();
    if (event.target.value.toString().startsWith('0')) {
      event.target.value = trimStart(event.target.value, '0');
    }

    let value = event.target.value;

    if (value.length > $scope.maxLength) {
      event.target.value = value.slice(0, $scope.maxLength);
    }

    $scope.person.id = event.target.value;
    $scope.currentGender = '';
    $scope.showGender = false;
    $scope.person.gender = '';
    $scope.showPriority = false;
    $scope.person.priority.category = null;
    resetPersonData();
    $scope.submitIdClicked = false;
  };

  $scope.setEnterprise = () => {
    $scope.enterprise = !$scope.enterprise;
    $scope.isLegalPerson = $scope.enterprise;
    $scope.currentPerson = $scope.enterprise ? PersonType.legalPerson : PersonType.physicalPerson;
    $scope.minLength = $scope.idLimits[$scope.currentPerson].min;
    $scope.maxLength = $scope.idLimits[$scope.currentPerson].max;
    $scope.currentGender = '';
    $scope.person.id = '';
    resetPersonData();
    $scope.person.idType = toLower($scope.isLegalPerson ? IdentificationType.CUIT : IdentificationType.DNI);
    $scope.submitIdClicked = false;
    if ($scope.enterprise) {
      $scope.showPriority = false;
      $scope.person.priority.category = null;
      const priority = {
        category: null,
      };
      $scope.person.priority = priority;
    }
  };

  $scope.setCurrentGender = (newGender) => {
    $scope.currentGender = Gender[camelCase(newGender)];
    $scope.person.gender = $scope.currentGender;
    resetPersonData();
    $scope.submitIdClicked = false;
  };

  const resetPersonData = () => {
    $scope.person = {
      ...$scope.person,
      firstName: '',
      lastName: '',
      companyName: '',
      phone: {
        countryCode: 'AR',
        full: '',
      },
      email: '',
    };
  };

  $scope.enableSearch = () => {
    const idLength = !isEmpty(get($scope, 'person.id', '')) ? $scope.person.id.length : 0;

    const currentPerson = get($scope, 'currentPerson', '');
    const isPhysicalPerson = currentPerson === PersonType.physicalPerson && (!$scope.showGender || !isEmpty($scope.currentGender));
    const isLegalPerson = $scope.isLegalPerson;
    const validId = idLength >= $scope.minLength && idLength <= $scope.maxLength;

    const enableSearch =
      ((isPhysicalPerson &&
        ($scope.showPriority ? !isEmpty(get($scope, 'person.priority.category', '')) : true)) ||
        isLegalPerson) &&
      validId;
    return enableSearch && !$scope.disabledIdentifiersDniOrCuit;
  };

  const setPersonPriority = () => {
    $scope.showPriority = !$scope.showPriority;
    $scope.person.priority.category = null;
  };

  $scope.setPriority = () => {
    setPersonPriority();
  };

  $scope.setPriorityCategory = (priorityCategory) => {
    $scope.person.priority.category = priorityCategory;
  };

  $scope.submitId = () => {
    if (!$scope.enableSearch()) return;

    let person = cloneDeep($scope.person);
    // Force temporary id type for initial validation, before send request to external service.
    // After external service responds, we take final identification type and overwrite this one.
    person.idType = toLower($scope.currentPerson === PersonType.legalPerson
        ? IdentificationType.CUIT
        : IdentificationType.DNI,
    );
    if(!ValidationService.validatePerson(person, 'personId', $scope, { timeOut: 10000 }, 'enqueue')) return;

    $scope.responseAll = null;

    AppointmentService.SearchWithSubscriptions(
      { id: person.id },
      $state.params.placeId,
      $state.params.searchFrom,
    ).then((appointmentResponse) => {
      localStorage.remove('notFoundMessage');

      if (appointmentResponse.data.length) {
        $scope.submitIdClicked = false;
        $state.go(SAFE_STATES.receptionAppointmentResults, { fields: { id: person.id } });
      } else {
        IdentificationService.Identify(
          person.id,
          person.gender,
          $state.params.organizationId,
          CalledFromEnum.Reception,
        )
          .then((identificationResponse) => {
            if (get(identificationResponse, 'moreThanOneUser', false) && !person.gender) {
              $scope.showGender = true;
              $scope.submitIdClicked = false;
              return;
            }
            if (get(identificationResponse, 'client', false)) {
              person = {
                ...person,
                firstName: get(identificationResponse, 'firstName', '') || '',
                lastName: get(identificationResponse, 'lastName', '') || '',
                companyName: get(identificationResponse, 'companyName', '') || '',
                idNumber: get(identificationResponse, 'idNumber', '') || '',
                gender: get(identificationResponse, 'gender', '') || '',
                idType: get(identificationResponse, 'idType', '') || ''
              };
            } else {
              person.idNumber = get(person, 'id', '') || '';
            }

            if (!person.priority.category) {
              delete person.priority;
            }

            localStorage.set('identificationData', person);
            $scope.submitIdClicked = false;
            $state.go(SAFE_STATES.receptionStep, { n: 0 });
          })
          .catch((error) => {
            $scope.submitIdClicked = false;
            console.log(error);
            // Show notification error??
          });
      }
    });
  };

  $scope.clearInput = () => {
    $scope.person.id = '';
  };

  const getValidatorId = (place, idType) => {
    const identifications = place.requirements.identifications;
    return identifications.find(id => id.key === idType);
  }

  const validateIdentificationsEnabled = (place) => {
    if (place.requirements.identifications) {
      place.requirements.identifications = place.requirements.identifications
        .filter(id => id.show)
        .map(id => {
          return id;
        });
    }

    const functionValidatorDniToString = get(getValidatorId(place, 'dni'), 'validator', '');
    const functionValidatorCuitToString = get(getValidatorId(place, 'cuit'), 'validator', '');
    $scope.disabledIdentifiersDniOrCuit = !functionValidatorDniToString || !functionValidatorCuitToString;
    if ($scope.disabledIdentifiersDniOrCuit) {
      toastrInstance = NotificationService.Error($translate.instant('unavailable-validations'), {
        timeOut: 0,
        preventDuplicates: true,
        preventOpenDuplicates: true,
        maxOpened: 5,
        closeButton: false,
        extendedTimeOut: 0,
        tapToDismiss: false,
      });
    } else {
      NotificationService.Clear(toastrInstance);
    }
  }

  validateIdentificationsEnabled(place);

  $scope.$on('$destroy', () => {
    NotificationService.Clear(toastrInstance);
  });

  $rootScope.$on('socketReady', async () => {
    const socket = await SocketService.GetInstance();
    socket.on('place:updated', place => {
      validateIdentificationsEnabled(place);
    });
  });
};

module.exports = module.controller('ReceptionWelcomeController', ReceptionWelcomeController);
